import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Clock, Send, CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import contactService, { ContactFormData } from '../services/contactService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import Navbar from '../components/layout/Navbar';
import StructuredFAQ from '../components/seo/StructuredFAQ';


const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // Debug logging
  React.useEffect(() => {
    console.log('ContactPage loaded - isAuthenticated:', isAuthenticated, 'user:', user?.email);
  }, [isAuthenticated, user]);
  
  const [formData, setFormData] = useState({
    name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '',
    email: user?.email || '',
    company: '',
    subject: '',
    message: '',
    inquiryType: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [referenceId, setReferenceId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const contactData: ContactFormData = {
        name: formData.name,
        email: formData.email,
        company: formData.company || undefined,
        subject: formData.subject,
        message: formData.message,
        inquiry_type: formData.inquiryType || undefined
      };

      const response = await contactService.submitContactForm(contactData);

      if (response.success) {
        setSubmitSuccess(true);
        setReferenceId(response.reference_id || null);
        toast.success(response.message);
        
        // Track lead conversion
        try {
          const { trackLead } = await import('../lib/metaPixel');
          trackLead('contact_form');
        } catch (error) {
          console.error('Failed to track lead conversion:', error);
        }

        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          message: '',
          inquiryType: ''
        });
      } else {
        toast.error('Failed to submit contact form. Please try again.');
      }
    } catch (error: any) {
      console.error('Contact form submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit contact form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };





  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-blue-500/30 to-purple-600/30 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute top-32 right-16 w-16 h-16 bg-gradient-to-br from-purple-500/25 to-pink-500/25 rounded-full blur-lg animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-16 w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="inline-block mb-6 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full border border-blue-200">
            <span className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              We're Here to Help
            </span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Get in Touch
          </h1>
          <p className="text-xl max-w-3xl mx-auto mb-8 text-gray-600 leading-relaxed">
            Have questions about Quixocial? Need help with your account? Want to explore partnership opportunities?
            We'd love to hear from you.
          </p>
        </div>
      </section>



      {/* Contact Form */}
      <section className="py-20 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Send Us a Message</h2>
            <p className="text-xl text-gray-600">
              Fill out the form below and we'll get back to you as soon as possible.
            </p>
          </div>

          <Card className="border-gray-200/50 shadow-2xl bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              {submitSuccess && referenceId && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                    <div>
                      <p className="text-green-800 dark:text-green-200 font-medium">Message sent successfully!</p>
                      <p className="text-green-600 dark:text-green-400 text-sm">Reference ID: {referenceId}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className="bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email address"
                      className="bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                      Company
                    </label>
                    <Input
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      placeholder="Enter your company name"
                      className="bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                      Inquiry Type
                    </label>
                    <Select onValueChange={(value) => handleInputChange('inquiryType', value)}>
                      <SelectTrigger className="bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                        <SelectValue placeholder="Select inquiry type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                        <SelectItem value="support">Technical Support</SelectItem>
                        <SelectItem value="sales">Sales Inquiry</SelectItem>
                        <SelectItem value="partnership">Partnership</SelectItem>
                        <SelectItem value="billing">Billing Question</SelectItem>
                        <SelectItem value="feature">Feature Request</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                    Subject *
                  </label>
                  <Input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Brief description of your inquiry"
                    className="bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                    Message *
                  </label>
                  <Textarea
                    required
                    rows={6}
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Please provide details about your inquiry..."
                    className="bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-sm" style={{ color: '#6B7280' }}>
                    * Required fields
                  </p>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="disabled:opacity-50"
                    style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.background = 'linear-gradient(to right, #2a9fd4, #3f2580)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)';
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>



      {/* Business Hours */}
      <section className="relative py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
          <div className="absolute bottom-32 right-20 w-40 h-40 bg-gradient-to-br from-emerald-400/15 to-cyan-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="hero-title text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#368EC7' }}>
              Business Hours
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our support team is available during these hours to assist you with any questions or concerns.
            </p>
          </motion.div>

          <motion.div
            className="group relative"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            {/* Outer border effect */}
            <div className="absolute -inset-0.5 rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-700" style={{ background: 'linear-gradient(135deg, #30b3dd, #4a298a)' }}></div>

            {/* Main card */}
            <div className="relative bg-white rounded-3xl border border-gray-200/50 group-hover:border-transparent shadow-2xl group-hover:shadow-3xl transition-all duration-700 overflow-hidden">
              {/* Animated background gradient */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.05), rgba(74, 41, 138, 0.05))' }}></div>

              {/* Header with icon */}
              <div className="relative p-8 pb-6" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}>
                <div className="flex items-center justify-center">
                  <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mr-6 group-hover:scale-110 transition-transform duration-300">
                    <Clock className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="text-3xl font-bold mb-2">Support Hours</h3>
                    <p className="text-blue-100 text-lg">We're here to help when you need us</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="relative p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Regular Hours</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100 hover:shadow-md transition-all duration-200">
                        <span className="text-gray-900 font-medium">Monday - Friday</span>
                        <span className="font-bold text-gray-900">9:00 AM - 6:00 PM EST</span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-xl border border-emerald-100 hover:shadow-md transition-all duration-200">
                        <span className="text-gray-900 font-medium">Saturday</span>
                        <span className="font-bold text-gray-900">10:00 AM - 4:00 PM EST</span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-100 hover:shadow-md transition-all duration-200">
                        <span className="text-gray-900 font-medium">Sunday</span>
                        <span className="font-bold text-red-600">Closed</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Response Times</h4>
                    <div className="space-y-4">
                      <div className="p-6 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200 hover:shadow-lg transition-all duration-300">
                        <div className="flex items-center mb-3">
                          <div className="w-4 h-4 bg-emerald-500 rounded-full mr-3"></div>
                          <span className="font-bold text-gray-900 text-lg">Priority Support</span>
                        </div>
                        <p className="text-gray-700 font-medium">Enterprise customers: Within 2 hours</p>
                      </div>
                      <div className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 hover:shadow-lg transition-all duration-300">
                        <div className="flex items-center mb-3">
                          <div className="w-4 h-4 rounded-full mr-3" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}></div>
                          <span className="font-bold text-gray-900 text-lg">Standard Support</span>
                        </div>
                        <p className="text-gray-700 font-medium">All customers: Within 24 hours</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section - SEO Optimized */}
      <section className="relative py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StructuredFAQ
            faqs={[
              {
                question: "How quickly can I get started with Quixocial?",
                answer: "You can sign up and start creating content immediately. Our onboarding process takes less than 5 minutes, and you'll have access to all features during your free trial."
              },
              {
                question: "Do you offer custom enterprise solutions?",
                answer: "Yes! We work with large organizations to provide custom integrations, dedicated support, and tailored features. Contact our sales team to discuss your specific needs."
              },
              {
                question: "What social media platforms do you support?",
                answer: "We currently support Facebook, Instagram, and we're constantly adding new platforms based on user demand."
              },
              {
                question: "Is my data secure with Quixocial?",
                answer: "Absolutely. We use enterprise-grade security measures including encryption, secure data centers, and regular security audits. Your data privacy is our top priority."
              }
            ]}
            title="Frequently Asked Questions"
            className=""
          />
        </div>
      </section>

      {/* CTA Section */}
      {/* CTA Section - Dark Mode */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {isAuthenticated ? (
            <>
              <h2 className="hero-title text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent">
                  Need More Help?
                </span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-300 mb-6 max-w-3xl mx-auto leading-relaxed">
                Check out our help center or return to your dashboard to continue creating amazing content.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center px-4 sm:px-0">
                <motion.div
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl blur opacity-30 group-hover:opacity-70 transition duration-500"></div>
                  <Button 
                    size="lg" 
                    className="relative w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-semibold shadow-lg hover:shadow-2xl rounded-2xl btn-interactive ripple overflow-hidden"
                    onClick={() => navigate("/app/help")}
                  >
                    <span className="relative z-10">Visit Help Center</span>
                  </Button>
                </motion.div>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-semibold rounded-2xl"
                  onClick={() => navigate("/app/dashboard")}
                >
                  Back to Dashboard
                </Button>
              </div>
            </>
          ) : (
            <>
              <h2 className="hero-title text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent">
                  Ready to Get Started?
                </span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-300 mb-6 max-w-3xl mx-auto leading-relaxed">
                Join early adopters using Quixocial to transform their brand presence.
              </p>
              <div className="flex justify-center px-4 sm:px-0">
                <Link to="/register" className="w-full sm:w-auto max-w-sm sm:max-w-none">
                  <motion.div
                    whileHover={{ scale: 1.05, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    className="group relative"
                  >
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl blur opacity-30 group-hover:opacity-70 transition duration-500"></div>
                    <Button
                      size="lg"
                      className="relative w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-semibold shadow-lg hover:shadow-2xl rounded-2xl btn-interactive ripple overflow-hidden"
                      style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #2a9fd4, #3f2580)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)';
                      }}
                    >
                      <span className="relative z-10">Start Free</span>
                      <ArrowRight className="ml-3 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                    </Button>
                  </motion.div>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div >
  );
};

export default ContactPage;